import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';

type DiffLine = { type: 'same' | 'add' | 'del'; text: string };

/** Line-level LCS diff; falls back to a plain before/after for very long texts. */
export function lineDiff(a: string, b: string): DiffLine[] | null {
  const x = a.split('\n');
  const y = b.split('\n');
  if (x.length * y.length > 250_000) return null;
  const dp: number[][] = Array.from({ length: x.length + 1 }, () => new Array(y.length + 1).fill(0));
  for (let i = x.length - 1; i >= 0; i--) {
    for (let j = y.length - 1; j >= 0; j--) {
      dp[i][j] = x[i] === y[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < x.length && j < y.length) {
    if (x[i] === y[j]) {
      out.push({ type: 'same', text: x[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      out.push({ type: 'del', text: x[i++] });
    } else {
      out.push({ type: 'add', text: y[j++] });
    }
  }
  while (i < x.length) out.push({ type: 'del', text: x[i++] });
  while (j < y.length) out.push({ type: 'add', text: y[j++] });
  return out;
}

export const DiffView: React.FC<{ before: string; after: string; maxHeight?: number }> = ({ before, after, maxHeight = 420 }) => {
  const diff = useMemo(() => lineDiff(before, after), [before, after]);
  if (!diff) return <Typography color="text.secondary">Too long to compare line by line.</Typography>;
  return (
    <Box
      component="pre"
      sx={{ m: 0, p: 1.5, maxHeight, overflow: 'auto', fontSize: 12.5, fontFamily: 'monospace', bgcolor: 'action.hover', borderRadius: 1, whiteSpace: 'pre-wrap' }}
    >
      {diff.map((l, i) => (
        <Box
          key={i}
          component="div"
          sx={{
            bgcolor: l.type === 'add' ? 'rgba(46,160,67,0.15)' : l.type === 'del' ? 'rgba(248,81,73,0.15)' : undefined,
            textDecoration: l.type === 'del' ? 'line-through' : undefined,
            color: l.type === 'same' ? 'text.secondary' : 'text.primary',
          }}
        >
          {l.type === 'add' ? '+ ' : l.type === 'del' ? '− ' : '  '}
          {l.text || ' '}
        </Box>
      ))}
    </Box>
  );
};
